import React, { useCallback } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import FormDate from "../../../../components/formFeild/FormDate";
import FormCheck from "../../../../components/formFeild/FormCheck";
import baseUrl from "../../../../api/baseUrl";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import { browserTimeZone } from "../../../../utils/colomboDate";

interface AddCategoryProps {
  closeModal: () => void;
}
interface FormData {
  startDate: string;
  endDate: string;
  payments: boolean;
}

const AllPaymentReport: React.FC<AddCategoryProps> = ({ closeModal }) => {
  const methods = useForm<FormData>();

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        const response = await baseUrl.get("client/payment/paymentReport", {
          responseType: "blob",
          params: {
            startDate: data.startDate,
            endDate: data.endDate,
            payments: data.payments,
            timeZone: browserTimeZone(),
          },
        });

        if (response.status === 204) {
          toast.info("No data found for the selected date range.");
          return;
        }

        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const url = URL.createObjectURL(pdfBlob);
        const fileName = `payment-report_${data.startDate}_${data.endDate}.pdf`;

        window.open(url, "_blank");

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        toast.success("Payment report generated.");
        closeModal();
      } catch (error: any) {
        if (error?.response?.status === 204) {
          toast.info("No data found for the selected date range.");
          return;
        }
        toast.error("Could not generate the payment report.");
        console.error("Error downloading the PDF", error);
      }
    },
    [closeModal]
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center">
              <DescriptionIcon className="text-primary" sx={{ fontSize: 18 }} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800 dark:text-slate-200">
                Payment Report
              </h2>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                Download PDF for a date range
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-600 hover:bg-slate-200 dark:hover:bg-slate-500 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        </div>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <FormDate label="From" name="startDate" />
              <FormDate label="To" name="endDate" />
            </div>
            <FormCheck label="Payment breakdown (sub-rows)" name="payments" />
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-sm"
            >
              <DownloadIcon sx={{ fontSize: 18 }} />
              Generate &amp; download PDF
            </button>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default AllPaymentReport;
